/**
 * coLivingKnobs — THE FRONTEND SOURCE OF TRUTH for the co-living return-math knobs, and
 * the mirror of functions/utils/underwritingKnobTables.js (the backend's).
 *
 * KEEP IN SYNC — the frontend can't import from functions/, which is the only reason this
 * copy exists. A vm-eval guard in functions/utils/_testCoLivingKnobs.js (run in the deploy
 * CI gate) asserts FE/BE equivalence over the knob table, a shared resolver case table, AND
 * the derived DEFAULT_CO_LIVING_UNDERWRITING — so a default changed on one side and not the
 * other fails the deploy rather than shipping a deal page that disagrees with the matcher.
 *
 * ⚠️ CHANGING A DEFAULT: edit the backend table, then mirror it here. Do NOT restate
 * defaults anywhere else — coLivingUnderwriting.js derives DEFAULT_CO_LIVING_UNDERWRITING
 * from this file, and the guard rejects a re-declared literal.
 *
 * These are also the per-company overrides, stored at
 * companies/{id}.data.features.coLivingUnderwriting in HUMAN units (percent, dollars,
 * years). resolveCoLivingKnobs converts them to the DECIMAL knob object that
 * computeCoLivingReturns (coLivingUnderwriting.js) consumes.
 */

export const CO_LIVING_UNDERWRITING_KNOBS = [
    { key: 'vacancyPct',              unit: 'percent', default: 10,  bounds: { min: 0, max: 100 } },
    { key: 'platformFeePct',          unit: 'percent', default: 11,  bounds: { min: 0, max: 100 } },
    { key: 'managementFeePct',        unit: 'percent', default: 0,   bounds: { min: 0, max: 100 } },
    { key: 'maintenancePct',          unit: 'percent', default: 8,   bounds: { min: 0, max: 100 } },
    // Utilities are a TWO-PART TARIFF — a per-house service charge plus a per-head volume
    // charge — because that is how the meters bill. Full derivation of the two numbers is in
    // functions/utils/underwritingKnobTables.js; keep the split, not just the totals, in sync.
    { key: 'utilitiesBaseMonthly',    unit: 'dollars', default: 400, bounds: { min: 0, max: 5000 } },
    { key: 'utilitiesPerRoomMonthly', unit: 'dollars', default: 60,  bounds: { min: 0, max: 2000 } },
    { key: 'cleaningLawnPestMonthly', unit: 'dollars', default: 200, bounds: { min: 0, max: 5000 } },
    { key: 'furnishingPerRoom',       unit: 'dollars', default: 700, bounds: { min: 0, max: 10000 } },
    { key: 'targetCapRate',           unit: 'percent', default: 7,   bounds: { min: 1, max: 20 } },
    { key: 'downPayment',             unit: 'percent', default: 20,  bounds: { min: 0, max: 100 } },
    { key: 'mortgageRate',            unit: 'percent', default: 7.5, bounds: { min: 0, max: 30 } },
    { key: 'loanTermYears',           unit: 'years',   default: 30,  bounds: { min: 1, max: 40 } },
    { key: 'closingCostPct',          unit: 'percent', default: 1.5, bounds: { min: 0, max: 10 } },
    { key: 'loanPointsPct',           unit: 'percent', default: 1,   bounds: { min: 0, max: 10 } },
    // ── Conversion / cash-to-buy line items (one-time). ALL default to a NO-OP (0)
    // so every company's cash-invested is byte-identical until it sets them. Mirror of
    // functions/utils/companyFeatures.js — the sync guard (_testCoLivingKnobs.js) asserts parity.
    { key: 'furnishingPerHouse',      unit: 'dollars', default: 0,   bounds: { min: 0, max: 100000 } },
    { key: 'costPerAddedBedroom',     unit: 'dollars', default: 0,   bounds: { min: 0, max: 100000 } },
    { key: 'costPerAddedBathroom',    unit: 'dollars', default: 0,   bounds: { min: 0, max: 100000 } },
    { key: 'inspectionBuffer',        unit: 'dollars', default: 0,   bounds: { min: 0, max: 100000 } },
    { key: 'holdingMonths',           unit: 'months',  default: 0,   bounds: { min: 0, max: 24 } },
    { key: 'closingCostFlat',         unit: 'dollars', default: 0,   bounds: { min: 0, max: 100000 } },
    // Filling in the pool — the ONE conversion line item that does not default to a no-op,
    // because it is a liability decision rather than an economics one. Charged only on a
    // CONFIRMED pool, so a company that never sees a pool house is unaffected. Full rationale
    // in the backend table (functions/utils/underwritingKnobTables.js); keep the two in sync.
    { key: 'poolFillCost',            unit: 'dollars', default: 5000, bounds: { min: 0, max: 100000 } },
    // ── Cash + hard money → rehab → DSCR refinance (the `bridgeRefi` financing mode).
    // Consumed ONLY by coLivingBridgeRefi.js; the DSCR-purchase path never reads them, so
    // every existing company's numbers are byte-identical until it switches a lead's mode.
    // Mirror of functions/utils/underwritingKnobTables.js — full rationale there.
    { key: 'bridgeRate',              unit: 'percent', default: 11,  bounds: { min: 0, max: 30 } },
    { key: 'bridgeLTC',               unit: 'percent', default: 85,  bounds: { min: 0, max: 100 } },
    { key: 'bridgePointsPct',         unit: 'percent', default: 2,   bounds: { min: 0, max: 10 } },
    { key: 'bridgeLenderFeeFlat',     unit: 'dollars', default: 2000, bounds: { min: 0, max: 100000 } },
    { key: 'bridgeRehabDrawPct',      unit: 'percent', default: 100, bounds: { min: 0, max: 100 } },
    { key: 'bridgeDrawAvgOutstandingPct', unit: 'percent', default: 50, bounds: { min: 0, max: 100 } },
    { key: 'bridgeMonths',            unit: 'months',  default: 6,   bounds: { min: 0, max: 24 } },
    { key: 'refinanceLTV',            unit: 'percent', default: 75,  bounds: { min: 0, max: 100 } },
    { key: 'refinanceRate',           unit: 'percent', default: 7.5, bounds: { min: 0, max: 30 } },
    { key: 'refinanceTermYears',      unit: 'years',   default: 30,  bounds: { min: 1, max: 40 } },
    { key: 'refinanceClosingCostPct', unit: 'percent', default: 2,   bounds: { min: 0, max: 10 } },
    { key: 'refinanceMinDscr',        unit: 'ratio',   default: 1.2, bounds: { min: 1, max: 3 } },
    // The wholesale fee carved out of the operator's own max offer before it reaches the seller.
    // NOT a cost of the deal — it never enters cash-invested, NOI or any verdict, only what the
    // SELLER is offered. Defaults to 0 (no-op); see the backend table for the full rationale.
    { key: 'wholesaleFee',            unit: 'dollars', default: 0,   bounds: { min: 0, max: 100000 } },
    // Your OWN agent's commission on the purchase, percent of price. Defaults to 0 (unrepresented
    // or seller-paid). The LISTING agent is deliberately absent — the seller pays that out of
    // their proceeds, so it is not the buyer's cash. See the backend table for the full rationale.
    { key: 'buyerAgentPct',           unit: 'percent', default: 0,   bounds: { min: 0, max: 10 } },
    // The RESALE-EXIT gate: total project cost as a percent of finished value (Cost-to-Value),
    // for a buyer who renovates, converts and SELLS rather than refinancing and holding. Read
    // ONLY by the cost-to-value MAO; the bridge→refi model never touches these. 80 is the strict
    // end of the operator band. See the backend table for the full rationale.
    { key: 'ctvTargetPct',            unit: 'percent', default: 80,  bounds: { min: 50, max: 120 } },
    // Seller's tax proration at the closing table, in months of the property's tax bill.
    { key: 'ctvProratedTaxMonths',    unit: 'months',  default: 3,   bounds: { min: 0, max: 12 } },
    // Appraisal + inspection spent before the deal is yours, $. 🪤 NOT `inspectionBuffer`, which
    // despite its name is a flat repair-line replacement and is already inside the project cost.
    { key: 'ctvDiligenceCost',        unit: 'dollars', default: 0,   bounds: { min: 0, max: 100000 } },
];

const CO_LIVING_UNDERWRITING_KNOBS_BY_KEY = CO_LIVING_UNDERWRITING_KNOBS.reduce((m, k) => { m[k.key] = k; return m; }, {});

export const coerceCoLivingUnderwritingKnob = (key, raw) => {
    const spec = CO_LIVING_UNDERWRITING_KNOBS_BY_KEY[key];
    if (!spec) return null;
    if (typeof raw !== 'number' || !Number.isFinite(raw)) return null;
    if (raw < spec.bounds.min || raw > spec.bounds.max) return null;
    return raw;
};

const knobToDecimal = (spec, humanValue) => (spec.unit === 'percent' ? humanValue / 100 : humanValue);

/**
 * Does this company COUNT the co-living conversion cost (furnishing + egress + build-out)
 * as cash invested? Default FALSE for every company — the line prices at $0 unless the
 * company sets features.coLivingChargeConversionCost = true.
 *
 * ⚠️ Zero does not mean the conversion is free, it means we are not pricing it: cash-invested
 * loses the line, so cash-on-cash and cap rate both read HIGH. Net cash flow is unaffected.
 * Mirror of functions/utils/companyFeatures.js#isCoLivingConversionCostOn — full rationale there.
 */
export const isCoLivingConversionCostOn = (features) => !!features && features.coLivingChargeConversionCost === true;

/**
 * Does this company price offers with the sliding max offer? Default FALSE. Mirror of
 * functions/utils/companyFeatures.js#isCoLivingSlidingMaoOn — full rationale there.
 */
export const isCoLivingSlidingMaoOn = (features) => !!features && features.coLivingSlidingMao === true;

/**
 * Does this company underwrite for co-living?
 *
 * Read as "cash flow is the number they judge a deal on". A company running
 * co-living holds its properties, so resale profit is not the verdict — a house
 * that loses money flipped can still be exactly the deal they want. Callers use
 * this to decide WHICH return to warn about, not whether co-living data exists
 * on a given lead (that's coLivingInterior + analysisRun.coLiving).
 */
export const isCoLivingOn = (features) => !!features && features.coLiving === true;

/**
 * DEFAULT_CO_LIVING_UNDERWRITING — the system defaults as the DECIMAL knob object
 * computeCoLivingReturns spreads over, DERIVED from the table above rather than restated.
 *
 * This is the frontend half of the single-source rule: coLivingUnderwriting.js used to
 * carry its own literal copy of these numbers, which meant a default lived in four places
 * (two here, two in functions/) and a partial edit shipped green — the deal-page
 * calculator would quote a buyer economics the ingestion gate and matcher disagreed with.
 * Nothing but the table above declares a co-living default on this side now.
 */
export const DEFAULT_CO_LIVING_UNDERWRITING = CO_LIVING_UNDERWRITING_KNOBS.reduce((acc, spec) => {
    acc[spec.key] = knobToDecimal(spec, spec.default);
    return acc;
}, {});

/**
 * Resolve the full co-living knob object (DECIMALS) for computeCoLivingReturns: each
 * company override (features.coLivingUnderwriting) merged over the system default.
 * `features` is the raw companies/{id}.data.features object.
 */
// VACANCY IS DELIBERATELY NOT MARKET-DERIVED — see the matching note in
// functions/utils/companyFeatures.js. We persist PadSplit's measured per-metro occupancy
// (coLivingInterior.marketInsights) and SHOW it, but the return math keeps using the
// company's own vacancy assumption rather than silently overwriting it per-deal.
export const resolveCoLivingKnobs = (features) => {
    const stored = (features && typeof features.coLivingUnderwriting === 'object' && features.coLivingUnderwriting) || {};
    const knobs = {};
    for (const spec of CO_LIVING_UNDERWRITING_KNOBS) {
        const override = coerceCoLivingUnderwritingKnob(spec.key, stored[spec.key]);
        const human = override != null ? override : spec.default;
        knobs[spec.key] = knobToDecimal(spec, human);
    }
    // NOT a knob (no unit, no bounds) — a boolean gate riding the knob object so every
    // consumer of computeCoLivingFurnishing inherits it. Mirror of
    // functions/utils/companyFeatures.js; the sync guard asserts parity.
    knobs.conversionCostOn = isCoLivingConversionCostOn(features);
    knobs.slidingMaoOn = isCoLivingSlidingMaoOn(features);
    return knobs;
};
